import { useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BillingPlanStatus } from "@api/shared-runtime/enums";
import { toast } from "sonner";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BillingPlanForm } from "./billing-plan-form";
import { BillingPlanHistory } from "./billing-plan-history";
import type { BillingPlanData } from "./billing-plan-values";

const PLAN_SUMMARY = "USD · Monthly cycles in New York";

export function BillingPlanDialog({
  organizationBusinessUnitId,
  label = "Billing plan",
}: {
  organizationBusinessUnitId: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    // The dialog renders through a portal, but React still bubbles its clicks
    // (including the overlay click that closes it) to this subtree. The
    // trigger usually sits inside a clickable row, so stop them here.
    <div className="contents" onClick={(event) => event.stopPropagation()}>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!busy) {
            setOpen(nextOpen);
          }
        }}
      >
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            {label}
          </Button>
        </DialogTrigger>
        <DialogContent
          className="max-h-[90dvh] overflow-y-auto [&.dialog-content]:w-[calc(100vw-2rem)] [&.dialog-content]:max-w-[760px]"
          showCloseButton={!busy}
          onInteractOutside={(event) => {
            if (busy) {
              event.preventDefault();
            }
          }}
        >
          {open && (
            <BillingPlanContent
              organizationBusinessUnitId={organizationBusinessUnitId}
              onSaved={() => setOpen(false)}
              onBusyChange={setBusy}
              busy={busy}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanDialogHeader({
  unit,
  action,
}: {
  unit?: BillingPlanData["unit"];
  action?: ReactNode;
}) {
  return (
    <DialogHeader className="pr-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <DialogTitle>Business-unit billing plan</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {unit && (
              <span className="font-medium text-foreground">
                {unit.organizationName} / {unit.name}
                {" · "}
              </span>
            )}
            {PLAN_SUMMARY}
          </DialogDescription>
        </div>
        {action}
      </div>
    </DialogHeader>
  );
}

function BillingPlanContent({
  organizationBusinessUnitId,
  onSaved,
  onBusyChange,
  busy,
}: {
  organizationBusinessUnitId: string;
  onSaved: () => void;
  onBusyChange: (busy: boolean) => void;
  busy: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const revealButtonRef = useRef<HTMLButtonElement>(null);
  // The editor remounts its form on every new plan version. Never refetch in
  // the background (window focus, reconnect) while the dialog is open, or a
  // version change from another admin or the New York midnight rollover would
  // silently discard unsaved edits. Explicit reloads and the post-save
  // invalidation still refresh the data; a stale version is caught server-side
  // and surfaced as a reload prompt.
  const query = useQuery(
    trpc.adminBillingPlans.getPlan.queryOptions(
      { organizationBusinessUnitId },
      { retry: false, staleTime: Infinity },
    ),
  );
  async function refreshPlan() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.adminBillingPlans.getPlan.queryKey({
          organizationBusinessUnitId,
        }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.adminUsageBilling.pathKey(),
      }),
    ]);
    toast.success("Billing plan saved");
    onSaved();
  }
  function reloadPlan() {
    void query.refetch();
  }
  // Cancel unmounts the focused button with the form, so hand focus to the
  // button that brings the form back instead of letting it fall to the dialog.
  function hideForm() {
    flushSync(() => setShowForm(false));
    revealButtonRef.current?.focus();
  }
  if (query.isPending) {
    return (
      <>
        <PlanDialogHeader />
        <p role="status" className="py-6 text-sm text-muted-foreground">
          Loading billing plan…
        </p>
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        <PlanDialogHeader />
        <div className="space-y-3 py-6">
          <p role="alert" className="text-sm text-red-700">
            {query.error.message}
          </p>
          <Button variant="outline" onClick={reloadPlan}>
            Retry
          </Button>
        </div>
      </>
    );
  }
  const data = query.data;
  const isArchived = Boolean(
    data.unit.deletedAt || data.unit.organizationDeletedAt,
  );
  const hasActivePlan = data.plans.some(
    (plan) => plan.status === BillingPlanStatus.Active,
  );
  // A unit without any plan has nothing to review, so the editor opens at
  // once. Otherwise the current terms come first and the editor is requested.
  const formVisible = data.canSave && (showForm || data.plans.length === 0);
  const canRequestForm = data.canSave && data.plans.length > 0 && !formVisible;
  return (
    <>
      <PlanDialogHeader
        unit={data.unit}
        action={
          canRequestForm && (
            <Button
              ref={revealButtonRef}
              size="sm"
              disabled={busy}
              onClick={() => setShowForm(true)}
            >
              {hasActivePlan ? "Schedule new terms" : "New plan"}
            </Button>
          )
        }
      />
      <div className="space-y-6 pt-4">
        <BillingPlanHistory
          data={data}
          actions={{
            onSaved: refreshPlan,
            onBusyChange,
            busy,
            onReload: reloadPlan,
          }}
        />
        {formVisible && (
          <BillingPlanForm
            key={data.version}
            data={data}
            onSaved={refreshPlan}
            onBusyChange={onBusyChange}
            onReload={reloadPlan}
            onCancel={showForm ? hideForm : undefined}
            autoFocus={showForm}
          />
        )}
        {!data.canSave && isArchived && (
          <p className="rounded-lg border bg-amber-50 p-4 text-sm text-amber-800">
            This business unit or its organization is archived. Its billing
            terms remain available for review.
          </p>
        )}
      </div>
    </>
  );
}
